import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/components/courses.css";
import "../styles/components/products.css";
import { homeContent } from "../content/homeContent";
import { getRandomGalleryImages } from "../content/galleryDriveIds";
import TestimonialsCarousel from "./TestimonialsCarousel";
import PromotionModal from "./promotionModal";
import useSEO from "../hooks/useSEO";

// CourseCard component
const CourseCard = ({ course }) => {
  // Map home content course IDs to actual course route IDs
  const courseRouteMap = {
    1: 'beginner-group-course',      // Beginner Korean Language Course -> actual beginner group course
    2: 'private-beginner-course',    // Private 1:1 Beginner -> private beginner course
    3: 'private-upper-beginner-course' // Private Upper Beginner -> upper beginner course
  };
  
  const routeId = courseRouteMap[course.id];
  
  // Render title and title2 if present
  return (
    <Link to={`/courses/${routeId}`} className="course-card-link">
      <div className="course-card">
        <img
          src={course.image}
          alt={course.title}
          className="course-image"
        />
        <div className="course-details">
          <h3 className="course-title">
            {course.title}
            {course.title2 && (<><br />{course.title2}</>)}
          </h3>
          <p className="course-description">{course.description}</p>
          <div className="course-meta">
            <span className="course-duration">{course.duration}</span>
            <span className="course-level">{course.level}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const TelegramCard = () => {
  return (
    <div className="telegram-card">
      <img
        src="/assets/telegram.png"
        alt="Telegram Community"
        className="telegram-image"
      />
      <div className="telegram-card-content">
        <h3 className="telegram-card-title">AALC Korean Learners</h3>
        <p className="telegram-card-description">Your supportive learning community awaits! Join 200+ passionate Korean learners sharing their journey.</p>
        <a
          href={homeContent.telegram.link}
          target="_blank"
          rel="noopener noreferrer"
          className="telegram-join-btn"
        >
          <span>Join Now</span>
          <span className="btn-icon"></span>
        </a>
        <div className="telegram-member-count">
          <span className="member-icon">👥</span>
          <span>200+ active members</span>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [resolvedGallery, setResolvedGallery] = useState([]);

  // SEO
  useSEO({
    title: "Learn Korean Online | Annyeong Aisyasya LC",
    description: "Beginner-friendly Korean classes in Malaysia. Small group and 1:1 lessons. Speak confidently in 5 weeks with passionate 선생님!"
  });

  useEffect(() => {
    let mounted = true;
    const placeholder = '/assets/placeholder.jpg';

    const preloadImage = (src) => {
      return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve({ ok: true, src });
        img.onerror = () => resolve({ ok: false, src: placeholder });
        img.src = src;
      });
    };

    (async () => {
      const images = getRandomGalleryImages(6);
      const results = await Promise.all(
        images.map(async (img) => {
          const res = await preloadImage(img.url);
          return {
            ...img,
            displayUrl: res.ok ? img.url : res.src
          };
        })
      );

      if (mounted) setResolvedGallery(results);
    })();

    return () => { mounted = false; };
  }, []);

  // Prepare placeholder fallback and the gallery items to render
  const placeholders = new Array(6).fill(0).map((_, i) => ({
    id: `ph-${i}`,
    caption: '',
    alt: 'placeholder',
    displayUrl: '/assets/placeholder.jpg'
  }));
  const galleryItems = (Array.isArray(resolvedGallery) && resolvedGallery.length > 0) ? resolvedGallery : placeholders;

  return (
    <div className="container">
  <PromotionModal />
      <main className="main-content">
        <section
          className="welcome-section home-welcome"
          style={{ backgroundImage: `url(/assets/korea-background.jpg)` }}
        >
            <h1>안녕하세요! Welcome to Your Korean Journey! 🌟</h1>
            <p className="subheading">
              안녕하세요! Ready to start your Korean journey?<br /><br />
              Speak Korean with confidence<br />
              Join a premium online academy that makes Korean learning fun!
            </p>
        </section>

        <section className="about-us">
          <div className="about-us-card">
            <div className="about-us-media">
              <div className="about-us-video-wrapper">
              <video className="about-us-video-el" controls poster="/assets/aisyah.png" autoPlay muted playsInline preload="auto">
                <source src="/assets/intro-vid.MOV" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              </div>
            </div>
            <div className="about-us-content">            
              <h2>About Us 🏠💜</h2>
              <p>
                안녕하세요! Welcome to AALC, where learning Korean becomes an enjoyable journey. Founded by passionate educators Aisyah and Syasya, we bring over 7 years of teaching experience to help <b>120+</b> Malaysian students master Korean language and culture with confidence.
              </p>
            </div>
          </div>
        </section>        {/* Updated Courses Section */}
        <section className="courses-section">
          <h2>Our Featured Courses 📚✨</h2>
          <div className="courses-list">
            {homeContent.courses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
          <div className="courses-section-footer">
            <Link to="/courses" className="find-out-more-btn">
              Find out more
            </Link>
          </div>
        </section>{/* Telegram Community Section */}
        <section className="telegram-section">
          <div className="container-narrow">
            <div className="section-header centered">
              <h2>Join Our Korean Learning Family</h2>
              <p>Connect with fellow Korean learners from Malaysia and beyond! Our vibrant Telegram community is where learning continues outside the classroom.</p>
            </div>              <div className="telegram-content">
              <div className="telegram-text">
                <div className="telegram-features-card">
                  <div className="telegram-features">
                    <div className="telegram-feature">
                      <div className="feature-icon-large">📚</div>
                      <div className="feature-content">
                        <h4>Free Learning Materials</h4>
                        <p>Access exclusive vocabulary lists, grammar guides, and practice exercises shared by your 선생님 (teachers)</p>
                      </div>
                    </div>
                    <div className="telegram-feature">
                      <div className="feature-icon-large">💬</div>
                      <div className="feature-content">
                        <h4>Practice with Peers</h4>
                        <p>Practice Korean with fellow students, ask questions, and get encouragement from the community</p>
                      </div>
                    </div>
                    <div className="telegram-feature">
                      <div className="feature-icon-large">🎯</div>
                      <div className="feature-content">
                        <h4>Daily Tips & Motivation</h4>
                        <p>Receive Korean tips, cultural insights, and motivational messages from Aisyah & Syasya 선생님</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="telegram-visual">
                <TelegramCard />
              </div>
            </div>
          </div>
        </section>

        <section className="section team">
          <h2>Meet Your 선생님 💜</h2>
          <div className="team-members">
            <article className="team-card">
              <img src={process.env.PUBLIC_URL + '/assets/aisyah.png'} alt="Aisyah" className="team-image" />
              <h3>Aisyah - Co-Founder & 선생님</h3>
              <p>👩‍🏫 Your caring Korean teacher who believes every student can succeed! I design fun and effective lessons in the curriculum that are tailored to help you learn efficiently while enjoying the process.  <br /> <br /> Mistakes? Totally normal! It’s part of learning! With a solid 7 years of teaching background, I’m here to help you grow in confidence and fluency, step by step.</p>
            </article>
            <article className="team-card">
              <img src={process.env.PUBLIC_URL + '/assets/syasya.png'} alt="Syasya" className="team-image" />
              <h3>Syasya - Co-Founder & 선생님</h3>
              <p>🎉 Your enthusiastic Korean learning cheerleader! With years of teaching both in Korea and Malaysia, I bring energy and fun to every lesson.  <br /> <br />I focus on practical language skills, clear explanations, and real cultural insights to help you feel confident and connected. Whether it’s for travel, work, or fun, I’m here to help you every step of the way!</p>
            </article>
          </div>        </section>

        <TestimonialsCarousel />

        <section className="professional-services">
          <div className="section-header centered">
            <h2>Our Professional Services 💼</h2>
            <p className="section-description">Beyond teaching, we offer premium translation and content services with our years of Korean language expertise.</p>
          </div>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">📄</div>
              <h3>Document Translation</h3>
              <p>Kor-Eng-Mal document translation (formal, legal, informal)</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🎬</div>
              <h3>Drama Subtitles</h3>
              <p>Professional subtitling for Korean dramas and entertainment content</p>
            </div>
            <div className="service-card">
              <div className="service-icon">📺</div>
              <h3>TV Commercial Translation</h3>
              <p>Commercial and advertising content translation with cultural context</p>
            </div>
            <div className="service-card">
              <div className="service-icon">⭐</div>
              <h3>K-Idol Commercial Translation</h3>
              <p>Specialized translation for K-pop and idol-related commercial content</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🎵</div>
              <h3>Song Translations</h3>
              <p>Lyric translation with poetic meaning and cultural nuances preserved</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🎙️</div>
              <h3>Voiceover/Dubbing Script</h3>
              <p>Script adaptation for voiceover and dubbing projects</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🎥</div>
              <h3>YouTube Video Subtitles</h3>
              <p>Professional subtitling for YouTube and social media content</p>
            </div>
            <div className="service-card">
              <div className="service-icon">📊</div>
              <h3>Brochures/Ads/Product Descriptions</h3>
              <p>Marketing material translation for business and e-commerce</p>
            </div>
            <div className="service-card">
              <div className="service-icon">💬</div>
              <h3>Social Media Content</h3>
              <p>Translation and localization for social media campaigns</p>
            </div>
          </div>
        </section>

        {/* Portfolio Gallery Section */}
        <section className="portfolio-gallery">
          <div className="section-header centered">
            <h2>Our Gallery 📸</h2>
          </div>
            <div className="gallery-grid">
              {/** Render from galleryItems so placeholders are used while preloading */}
                {galleryItems.map((item, index) => (
                <div className="gallery-item" key={`${item.id}-${index}`}>
                  <img
                    src={item.displayUrl}
                    alt={item.alt}
                    loading="lazy"
                  />
                  <div className="gallery-overlay">
                    <span className="gallery-caption">{item.caption}</span>
                  </div>
                </div>
              ))}
            </div>
        </section>

        {/* Announcement Section */}
        <section className="announcement-section">
          <div className="announcement-container">
            <div className="announcement-content">
              <div className="announcement-icon">📢</div>
              <div className="announcement-body">
                <h2>Ready to Get Started?</h2>
                <p>Have a translation project in mind? Need Korean language services? We're here to help bring your content to life with professional quality and cultural accuracy.</p>
              </div>
              <div className="announcement-cta">
                <Link to="/contact" className="announcement-btn">
                  <span>Get in Touch</span>
                  <span className="btn-arrow">→</span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
